import { useEffect, useRef, useState, type ComponentType } from "react";
import { Megaphone, MessageCircle, Palette, ScanLine, Sparkles, Wallet } from "lucide-react";
import { cn } from "@/lib/utils";
import { NAV, normalisePath, whatsappLink } from "@/lib/site";

const ICONS: Record<string, ComponentType<{ className?: string; strokeWidth?: number }>> = {
  look: Sparkles,
  transformation: ScanLine,
  art: Palette,
  ad: Megaphone,
  services: Wallet,
  book: MessageCircle,
};

type BottomNavProps = {
  /** True while the lightbox is open, so the bar can get out of the way. */
  hidden?: boolean;
};

export function BottomNav({ hidden = false }: BottomNavProps) {
  const [active, setActive] = useState<string>("look");
  const sectionRefs = useRef(new Map<string, HTMLElement>());

  // Standalone pages cannot be scroll-spied, so their tab is active by URL.
  const [route, setRoute] = useState(() => normalisePath(window.location.pathname));

  useEffect(() => {
    const sync = () => setRoute(normalisePath(window.location.pathname));
    window.addEventListener("popstate", sync);
    return () => window.removeEventListener("popstate", sync);
  }, []);

  // Track which section owns the viewport so the active tab is always correct.
  // Route items are skipped on purpose: their tab navigates away from this page,
  // so a section here must never claim to own them.
  useEffect(() => {
    const ids = NAV.filter(n => !n.route).map(n => n.id);

    const observer = new IntersectionObserver(
      entries => {
        // Pick the entry closest to filling the viewport.
        let best: IntersectionObserverEntry | null = null;
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          if (!best || entry.intersectionRatio > best.intersectionRatio) best = entry;
        }
        if (best) setActive(best.target.id);
      },
      // A band across the middle of the screen decides "what am I looking at".
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 },
    );

    for (const id of ids) {
      const el = document.getElementById(id);
      if (el) {
        sectionRefs.current.set(id, el);
        observer.observe(el);
      }
    }

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <nav
        aria-label="Sections"
        className={cn(
          "safe-bottom fixed inset-x-0 bottom-0 z-40 border-t border-border/70 bg-background/92 backdrop-blur-lg transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] lg:hidden",
          hidden ? "translate-y-full" : "translate-y-0",
        )}
      >
        <ul className="grid h-16" style={{ gridTemplateColumns: `repeat(${NAV.length}, minmax(0, 1fr))` }}>
          {NAV.map(item => {
            const Icon = ICONS[item.id] ?? Sparkles;
            const isActive = item.route ? route === item.href : active === item.id;

            const inner = (
              <>
                <span className="relative flex h-6 w-6 items-center justify-center">
                  {isActive && (
                    <span className="animate-pulse-ring absolute inset-0 rounded-full bg-gold/40" />
                  )}
                  <Icon
                    className={cn(
                      "relative h-[1.15rem] w-[1.15rem] transition-colors duration-300",
                      isActive ? "text-gold" : "text-muted-foreground",
                    )}
                    strokeWidth={1.5}
                  />
                </span>
                <span
                  className={cn(
                    "font-sans text-[0.5625rem] tracking-[0.14em] uppercase transition-colors duration-300",
                    isActive ? "text-gold" : "text-muted-foreground",
                  )}
                >
                  {item.short}
                </span>
              </>
            );

            const base =
              "flex cursor-pointer flex-col items-center justify-center gap-1.5 py-2.5 transition-colors duration-300 active:opacity-60";

            return (
              <li key={item.id}>
                {item.action === "book" ? (
                  <a
                    href={whatsappLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-current={isActive ? "true" : undefined}
                    className={base}
                  >
                    {inner}
                  </a>
                ) : (
                  <a
                    href={item.href}
                    aria-current={isActive ? "true" : undefined}
                    className={base}
                  >
                    {inner}
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      </nav>
    </>
  );
}
